import React, { ReactNode, useEffect, useState } from "react";
import styled, { css, keyframes, Keyframes } from "styled-components";

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

const PRESET: Record<Preset, StyledMountAnimationConfig> = {
  crossFade: {
    $mountKeyframes: fadeIn,
    $mountTiming: 1000,
    $unmountKeyframes: fadeOut,
    $unmountTiming: 1000,
  },
};

const StyledDiv = styled.div<{
  $show: boolean;
  $mountKeyframes: Keyframes;
  $mountTiming: number;
  $unmountKeyframes: Keyframes;
  $unmountTiming: number;
}>`
  animation: ${({
    $show,
    $mountKeyframes,
    $mountTiming,
    $unmountKeyframes,
    $unmountTiming,
  }) =>
    $show
      ? css`
          ${$mountKeyframes} ${$mountTiming}ms ease
        `
      : css`
          ${$unmountKeyframes} ${$unmountTiming}ms ease
        `};
  position: "relative";
`;

export type Preset = "crossFade";

export type MountAnimationConfig = {
  mountKeyframes: Keyframes;
  mountTiming: number;
  unmountKeyframes: Keyframes;
  unmountTiming: number;
};

type StyledMountAnimationConfig = {
  $mountKeyframes: Keyframes;
  $mountTiming: number;
  $unmountKeyframes: Keyframes;
  $unmountTiming: number;
};

export type MountTransitionProps =
  | {
      children: ReactNode;
      preset: Preset;
      show: boolean;
    }
  | (MountAnimationConfig & {
      children: ReactNode;
      preset: null;
      show: boolean;
    });

const MountTransition = (props: MountTransitionProps) => {
  const { children, preset, show } = props;
  const [render, setRender] = useState(show);

  useEffect(() => {
    if (show) setRender(true);
  }, [show]);

  const onAnimationEnd = () => {
    if (!show) setRender(false);
  };

  if (preset) {
    return (
      <StyledDiv
        {...PRESET[preset]}
        onAnimationEnd={onAnimationEnd}
        $show={show}
      >
        {children}
      </StyledDiv>
    );
  }

  const { mountKeyframes, mountTiming, unmountKeyframes, unmountTiming } =
    props;

  return render ? (
    <StyledDiv
      onAnimationEnd={onAnimationEnd}
      $show={show}
      $mountKeyframes={mountKeyframes}
      $mountTiming={mountTiming}
      $unmountKeyframes={unmountKeyframes}
      $unmountTiming={unmountTiming}
    >
      {children}
    </StyledDiv>
  ) : null;
};

export default MountTransition;
